import { computed, watch, type Ref } from "vue"
import { useI18n } from "vue-i18n"
import { AssetType, type BuiltSubcategory, LandCategory, LandSubcategory } from "@/graphql"
import { builtCategoryValues, builtSubcategories as builtSubcategoryValues } from "@/utils/builtAssetClassification"

const landSubcategoriesByCategory: Partial<Record<LandCategory, LandSubcategory[]>> = {
	[LandCategory.Residential]: [
		LandSubcategory.LowRise,
		LandSubcategory.MidRise,
		LandSubcategory.HighRise,
		LandSubcategory.TownHouse,
		LandSubcategory.SingleFamily,
	],
	[LandCategory.Commercial]: [LandSubcategory.LowRise, LandSubcategory.MidRise, LandSubcategory.HighRise],
	[LandCategory.Industrial]: [LandSubcategory.LightMedium, LandSubcategory.Heavy],
	[LandCategory.Agricultural]: [
		LandSubcategory.FullyCultivated,
		LandSubcategory.MostlyCultivated,
		LandSubcategory.HalfAndHalf,
		LandSubcategory.MostlyForestry,
		LandSubcategory.FullForestry,
	],
}

const landSubcategoryKeys = Object.fromEntries(Object.entries(LandSubcategory).map(([key, value]) => [value, key]))

enum ClassificationKind {
	Land = "land",
	Built = "built",
}

type ClassificationOption = { value: string; label: string; group?: string }

function toOptionValue(kind: ClassificationKind, value: string): string {
	return `${kind}:${value}`
}

function valuesOfKind(optionValues: string[], kind: ClassificationKind): string[] {
	const prefix = `${kind}:`
	return optionValues.filter(value => value.startsWith(prefix)).map(value => value.slice(prefix.length))
}

export function useTransactionClassificationFilter(filters: {
	assetTypes: Ref<string[]>
	landCategories: Ref<string[]>
	landSubcategories: Ref<string[]>
	builtCategories: Ref<string[]>
	builtSubcategories: Ref<string[]>
}) {
	const { t } = useI18n()
	const { assetTypes, landCategories, landSubcategories, builtCategories, builtSubcategories } = filters

	const hasLand = computed(() => assetTypes.value.includes(AssetType.Land))
	const hasBuilt = computed(() => assetTypes.value.includes(AssetType.Built))

	const isMixed = computed(() => hasLand.value && hasBuilt.value)

	function kindGroup(kind: ClassificationKind): { group?: string } {
		return isMixed.value ? { group: t(`property.assetType.${kind}`) } : {}
	}

	function kindLabel(land: string, built: string, mixed: string): string {
		if (isMixed.value || (!hasLand.value && !hasBuilt.value)) return t(mixed)
		return t(hasLand.value ? land : built)
	}

	const landSubcategoryValues = computed(() => [
		...new Set(landCategories.value.flatMap(category => landSubcategoriesByCategory[category as LandCategory] ?? [])),
	])
	const builtSubcategoryOptionValues = computed(() => builtSubcategoryValues(builtCategories.value))

	const categoryOptions = computed<ClassificationOption[]>(() => [
		...(hasLand.value ? Object.values(LandCategory) : []).map(value => ({
			value: toOptionValue(ClassificationKind.Land, value),
			label: t(`property.landCategory.${value}`),
			...kindGroup(ClassificationKind.Land),
		})),
		...(hasBuilt.value ? builtCategoryValues : []).map(value => ({
			value: toOptionValue(ClassificationKind.Built, value),
			label: t(`property.builtCategory.${value}`),
			...kindGroup(ClassificationKind.Built),
		})),
	])

	const subcategoryOptions = computed<ClassificationOption[]>(() => [
		...landSubcategoryValues.value.map(value => ({
			value: toOptionValue(ClassificationKind.Land, value),
			label: t(`property.landSubcategory.${landSubcategoryKeys[value]}`),
			...kindGroup(ClassificationKind.Land),
		})),
		...builtSubcategoryOptionValues.value.map(value => ({
			value: toOptionValue(ClassificationKind.Built, value),
			label: t(`property.builtSubcategory.${value}`),
			...kindGroup(ClassificationKind.Built),
		})),
	])

	const categoryLabel = computed(() => kindLabel("filters.land-category", "filters.built-category", "filters.category"))
	const subcategoryLabel = computed(() =>
		kindLabel("filters.land-subcategory", "filters.built-subcategory", "filters.subcategory")
	)

	const categories = computed<string[]>({
		get: () => [
			...landCategories.value.map(value => toOptionValue(ClassificationKind.Land, value)),
			...builtCategories.value.map(value => toOptionValue(ClassificationKind.Built, value)),
		],
		set: values => {
			landCategories.value = valuesOfKind(values, ClassificationKind.Land)
			builtCategories.value = valuesOfKind(values, ClassificationKind.Built)
		},
	})

	const subcategories = computed<string[]>({
		get: () => [
			...landSubcategories.value.map(value => toOptionValue(ClassificationKind.Land, value)),
			...builtSubcategories.value.map(value => toOptionValue(ClassificationKind.Built, value)),
		],
		set: values => {
			landSubcategories.value = valuesOfKind(values, ClassificationKind.Land)
			builtSubcategories.value = valuesOfKind(values, ClassificationKind.Built)
		},
	})

	const isCategoryDisabled = computed(() => !hasLand.value && !hasBuilt.value)
	const isSubcategoryDisabled = computed(() => subcategoryOptions.value.length === 0)

	watch([hasLand, hasBuilt], () => {
		if (!hasLand.value) {
			landCategories.value = []
			landSubcategories.value = []
		}
		if (!hasBuilt.value) {
			builtCategories.value = []
			builtSubcategories.value = []
		}
	})

	watch(landSubcategoryValues, validValues => {
		landSubcategories.value = landSubcategories.value.filter(value => validValues.includes(value as LandSubcategory))
	})

	watch(builtSubcategoryOptionValues, validValues => {
		builtSubcategories.value = builtSubcategories.value.filter(value => validValues.includes(value as BuiltSubcategory))
	})

	return {
		categories,
		subcategories,
		categoryOptions,
		subcategoryOptions,
		categoryLabel,
		subcategoryLabel,
		isCategoryDisabled,
		isSubcategoryDisabled,
	}
}
